import { handleAxiosError } from '@/api/helpers';
import { client } from '@/client';
import { Account } from '@/client/accounts';
import { SystemRoles } from '@/client/users';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { AccountSelectInput } from '@/components/form/selectors';
import { useAppDispatch, useAppSelector } from '@/hooks/store';
import { usePosthogTracking } from '@/hooks/usePosthogTracking';
import { useToast } from '@/hooks/useToast';
import {
  selectCurrentAccount,
  setCurrentAccount,
} from '@/store/features/account';
import {
  loadCurrentUserAction,
  selectCurrentUser,
} from '@/store/features/users';
import {
  buildPostHogGlobalProperties,
  buildPostHogGroupProperties,
} from '@/utils/posthog';
import { AxiosError } from 'axios';
import { usePostHog } from 'posthog-js/react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Md5 } from 'ts-md5';

const StyledDiv = styled.div`
  margin: 0 var(--medium-padding) var(--small-padding);
`;

export const SidebarAccountSelect: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const account = useAppSelector(selectCurrentAccount);
  const currentUser = useAppSelector(selectCurrentUser);
  const navigate = useNavigate();
  const toast = useToast();
  const posthog = usePostHog();
  const { allowTracking } = usePosthogTracking();
  const [selectorKey, setSelectorKey] = useState(0);

  const onSelect = async (account?: Account) => {
    if (account) {
      try {
        // Get the current account in case of data that is not send in the list of accounts
        const response = await client.accounts.getAccount(account.id);

        if (!response) {
          toast?.error(
            t('account.change.missing'),
            t('account.change.missing.message'),
          );
          setSelectorKey((key) => key + 1);
          return;
        }

        // Posthog integration.
        if (currentUser && allowTracking && response.allowPosthogTracking) {
          // On account switch, refresh only current account context properties.
          // Initial login snapshot fields are set in AuthLoadUser.
          const globalPostHogProperties = buildPostHogGlobalProperties(
            response,
            currentUser,
          );

          posthog.setPersonProperties(globalPostHogProperties);
          posthog.group(
            'account',
            Md5.hashStr(response.id),
            buildPostHogGroupProperties(response),
          );
          posthog.register(globalPostHogProperties);
        }

        dispatch(setCurrentAccount(response));
        if (currentUser?.role.code === SystemRoles.MULTIPLE_ACCOUNTS_ADMIN) {
          loadCurrentUserAction(dispatch);
        }
        navigate(RedirectPaths[RedirectPathsEnum.ADMIN_DASHBOARD]());
      } catch (e) {
        handleAxiosError(e as AxiosError, toast);
        setSelectorKey((key) => key + 1);
      }
    }
  };

  return (
    <StyledDiv>
      {account && (
        <AccountSelectInput
          key={selectorKey}
          className="w-full is-purple account-selector"
          defaultValue={account}
          onSelect={onSelect}
          forceRefetch={false}
          isPurple
          ignoreDemoMode={true}
        />
      )}
    </StyledDiv>
  );
};
